import { Button, Tooltip } from '@dev-spendesk/grapes';

import { IconButtonText } from 'common/components/IconButtonText';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompany } from 'modules/app/hooks/useCompany';
import { useUser } from 'modules/app/hooks/useUser';
import { useNotifications } from 'modules/app/notifications';
import { getCardAccess } from 'modules/physical-cards/utils/cardBlockingReasons';

import { type ApiCard } from '../../card';
import { doCardAction, getCardBlockingAction } from '../../utils/card';

type Props = {
  card: ApiCard;
  isCardBlocked: boolean;
  isCardBeingBlockedOrUnblocked: boolean;
  setIsCardBeingBlockedOrUnblocked: (
    isCardBeingBlockedOrUnblocked: boolean,
  ) => void;
  onProcessed: (updatedCard?: ApiCard) => void;
  smallButton?: boolean;
};

export const BlockUnblock = ({
  card,
  isCardBlocked,
  isCardBeingBlockedOrUnblocked,
  setIsCardBeingBlockedOrUnblocked,
  onProcessed,
  smallButton,
}: Props) => {
  const { t } = useTranslation('global');
  const { successNotif, dangerNotif } = useNotifications();

  const company = useCompany();
  const user = useUser();

  const cardsAccess = getCardAccess(user.id, card.blocking_reason);

  const ownCardHasBeenBlockedBySomeoneElse =
    isCardBlocked &&
    card.user_id === user.id &&
    card.blocker_id !== user.id &&
    !user.is_account_owner &&
    !user.is_admin;

  const isCardsAccessRestrictedByProcess =
    cardsAccess?.hasAccess === false && cardsAccess.initiator !== 'user';

  const isNonActivatedSfsCard =
    card.banking_provider === 'sfs' && card.status === 'PRE';

  const isButtonDisabled =
    isCardsAccessRestrictedByProcess ||
    isCardBeingBlockedOrUnblocked ||
    ownCardHasBeenBlockedBySomeoneElse ||
    isNonActivatedSfsCard;

  let blockUnblockCtaText;
  if (isCardBeingBlockedOrUnblocked) {
    blockUnblockCtaText = isCardBlocked
      ? t('cards.loadingUnBlocking')
      : t('cards.loadingBlocking');
  } else {
    blockUnblockCtaText = isCardBlocked
      ? t('cards.actions.unblock')
      : t('cards.actions.block');
  }

  const blockOrUnblockCard = async () => {
    if (!isCardBeingBlockedOrUnblocked) {
      setIsCardBeingBlockedOrUnblocked(true);
      const actionType = getCardBlockingAction(card);

      return doCardAction(
        company.id,
        card.id,
        actionType,
        null,
        async (error, updatedCard) => {
          if (!error && updatedCard) {
            await onProcessed(updatedCard);

            setIsCardBeingBlockedOrUnblocked(false);
            const message =
              updatedCard.status === 'BLO'
                ? t('cards.actions.successBlock')
                : t('cards.actions.successUnblock');
            successNotif(message);
          } else if (error) {
            await onProcessed();

            setIsCardBeingBlockedOrUnblocked(false);
            const message = error.registered
              ? `${
                  error.name === 'BankableError'
                    ? t('errors:registry.BankableError')
                    : t('errors:somethingWrong')
                } (${t('errors:code')} ${error.name})`
              : t('errors:somethingWrong');
            dangerNotif(message);
          }
        },
      );
    }
  };

  return (
    <Tooltip
      content={
        card.blocker?.fullname
          ? t('cards.disabledActionBlocked3rdPartyWithName', {
              name: card.blocker.fullname,
            })
          : t('cards.disabledActionBlockedCardTip')
      }
      isDisabled={!ownCardHasBeenBlockedBySomeoneElse}
      triggerAsChild
    >
      {smallButton ? (
        <IconButtonText
          isDisabled={isButtonDisabled}
          onClick={blockOrUnblockCard}
          text={blockUnblockCtaText}
          iconName="lock-close"
        />
      ) : (
        <Button
          fit="parent"
          text={blockUnblockCtaText}
          variant="secondaryAlert"
          isDisabled={isButtonDisabled}
          isLoading={isCardBeingBlockedOrUnblocked}
          onClick={blockOrUnblockCard}
        />
      )}
    </Tooltip>
  );
};
